<template>
  <div class="p2p-page">
    <div class="p2p-stack" style="gap:6px">
      <h1 class="p2p-h1">Posts</h1>
      <p class="p2p-lead">What is scheduled, what went out, and anything that needs you.</p>
    </div>
    <div class="p2p-tabs" role="tablist">
      <button v-for="item in tabs" :key="item.key" type="button" role="tab" :aria-selected="tab === item.key ? 'true' : 'false'" :class="{ on: tab === item.key }" @click="setTab(item.key)">{{ item.label }}</button>
    </div>
    <div class="p2p-filters" role="search">
      <label class="p2p-label p2p-filter-grow">Search
        <input v-model="search" type="search" class="p2p-input" placeholder="Words in the post or project name" @input="queue">
      </label>
      <label class="p2p-label">Network
        <select v-model="platform" class="p2p-select" @change="reload">
          <option value="">All networks</option>
          <option v-for="key in networks" :key="key" :value="key">{{ labels[key] }}</option>
        </select>
      </label>
      <label class="p2p-label">Project
        <select v-model="projectId" class="p2p-select" @change="reload">
          <option value="">All projects</option>
          <option v-for="project in projects" :key="project.id" :value="String(project.id)">{{ project.name }}</option>
        </select>
      </label>
      <label class="p2p-label">From<input v-model="from" type="date" class="p2p-input" @change="reload"></label>
      <label class="p2p-label">To<input v-model="to" type="date" class="p2p-input" @change="reload"></label>
      <button v-if="filtered" type="button" class="p2p-btn sm ghost" style="align-self:flex-end;margin-bottom:6px" @click="clear">Clear</button>
    </div>
    <div v-if="tab === 'all'" class="p2p-faint">Drafts live in each project, and autopilot posts waiting for you are in <router-link to="/approvals">Approvals</router-link>.</div>
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    <div v-if="loading" class="p2p-skeleton" style="height:200px"></div>
    <div v-else-if="!deliveries.length" class="p2p-empty">
      <template v-if="filtered"><strong>No posts match</strong><span>Try other filters.</span>
        <button type="button" class="p2p-btn sm soft" @click="clear">Clear filters</button></template>
      <template v-else><strong>{{ empty.title }}</strong><span>{{ empty.text }}</span>
        <router-link to="/create" class="p2p-btn">Create posts</router-link></template>
    </div>
    <div v-else class="p2p-stack" style="gap:12px">
      <article v-for="group in groups" :key="group.key" class="p2p-card tight p2p-post-group">
        <div class="p2p-row" style="flex-wrap:nowrap;gap:12px">
          <MediaImg v-if="group.thumb" :src="group.thumb" ratio="4 / 5" class="p2p-delivery-thumb" />
          <span v-else class="p2p-delivery-thumb text"><v-icon small>mdi-text</v-icon></span>
          <div class="p2p-stack" style="gap:2px;min-width:0;flex:1 1 auto">
            <div class="p2p-h3" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ group.hook }}</div>
            <div class="p2p-faint">
              <router-link :to="'/projects/' + group.projectId">{{ group.projectName }}</router-link>
              · {{ group.rows.length }} account{{ group.rows.length === 1 ? '' : 's' }}<span v-if="group.summary"> · {{ group.summary }}</span>
            </div>
          </div>
        </div>
        <div class="p2p-stack" style="gap:8px">
          <DeliveryRow v-for="delivery in group.rows" :key="delivery.id" :delivery="delivery" compact @changed="reload" />
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import DeliveryRow from '../components/DeliveryRow.vue';
import MediaImg from '../components/MediaImg.vue';
import { LABELS } from '../components/NetBadge.vue';
import { PLATFORM_ORDER } from '../components/steps/StepAccounts.vue';
import { get, message } from '../api';
import { poller } from '../poll';

const EMPTY = { upcoming: { title: 'Nothing scheduled', text: 'Schedule posts or turn on autopilot and they appear here.' },
  published: { title: 'Nothing published yet', text: 'Published posts appear here with a link to each one.' },
  attention: { title: 'All good', text: 'Failed posts and posts waiting for credits appear here.' },
  cancelled: { title: 'Nothing cancelled', text: 'Posts you cancel or retry appear here.' },
  all: { title: 'No posts yet', text: 'Create your first posts from your product link.' } };
const TABS = ['upcoming', 'published', 'attention', 'cancelled', 'all'];

function dayStart(value) { return value ? new Date(value + 'T00:00:00').toISOString() : undefined; }

export default {
  name: 'Posts',
  components: { DeliveryRow, MediaImg },
  data: () => ({ deliveries: [], projects: [], loading: true, error: '', loop: null, timer: null, ticket: 0, labels: LABELS, networks: PLATFORM_ORDER,
    search: '', platform: '', projectId: '', from: '', to: '',
    tabs: [{ key: 'upcoming', label: 'Upcoming' }, { key: 'published', label: 'Published' }, { key: 'attention', label: 'Needs attention' },
      { key: 'cancelled', label: 'Cancelled' }, { key: 'all', label: 'All' }] }),
  computed: {
    tab() { return TABS.indexOf(this.$route.query.tab) >= 0 ? this.$route.query.tab : 'upcoming'; },
    empty() { return EMPTY[this.tab]; },
    filtered() { return !!(this.search.trim() || this.platform || this.projectId || this.from || this.to); },
    groups() {
      const groups = [];
      const index = {};
      this.deliveries.forEach(delivery => {
        const key = delivery.publish_group + ':' + delivery.post_id;
        if (!index[key]) {
          // What was sent (or will be): the post itself may have been edited since.
          const sent = String(delivery.caption || '').trim().split('\n')[0];
          index[key] = { key, rows: [], hook: sent || delivery.hook || 'Deleted post', thumb: delivery.thumb_url,
            projectId: delivery.project_id, projectName: delivery.project_name };
          groups.push(index[key]);
        }
        index[key].rows.push(delivery);
      });
      groups.forEach(group => {
        const count = state => group.rows.filter(row => row.state === state).length;
        const parts = [];
        if (count('published')) parts.push(count('published') + ' published');
        if (count('failed')) parts.push(count('failed') + ' failed');
        if (count('published') && count('failed')) parts.unshift('partly published');
        group.summary = parts.join(', ');
      });
      return groups;
    }
  },
  watch: { tab() { this.start(); } },
  mounted() {
    this.start();
    get('/projects').then(data => { this.projects = data.projects; }).catch(() => {});
  },
  beforeDestroy() { if (this.loop) this.loop.stop(); clearTimeout(this.timer); },
  methods: {
    setTab(key) { this.$router.replace({ query: { tab: key } }).catch(() => {}); },
    queue() { clearTimeout(this.timer); this.timer = setTimeout(this.reload, 350); },
    clear() { this.search = ''; this.platform = ''; this.projectId = ''; this.from = ''; this.to = ''; this.reload(); },
    reload() { this.start(); },
    start() {
      if (this.loop) this.loop.stop();
      this.loading = !this.deliveries.length;
      this.loop = poller(async () => { await this.load(); return this.deliveries.some(item => ['queued', 'publishing'].indexOf(item.state) >= 0) ? 3000 : 30000; });
      this.loop.start();
    },
    async load() {
      // Filters can change while a request is out; only the latest answer is shown.
      const ticket = ++this.ticket;
      const params = { tab: this.tab };
      if (this.search.trim()) params.q = this.search.trim();
      if (this.platform) params.platform = this.platform;
      if (this.projectId) params.project_id = this.projectId;
      if (this.from) params.since = dayStart(this.from);
      if (this.to) params.until = new Date(new Date(this.to + 'T00:00:00').getTime() + 24 * 3600 * 1000).toISOString();
      try {
        const data = await get('/deliveries', params);
        if (ticket !== this.ticket) return;
        this.deliveries = data.deliveries;
        this.error = '';
      } catch (error) {
        if (ticket === this.ticket) this.error = message(error);
      } finally {
        if (ticket === this.ticket) this.loading = false;
      }
    }
  }
};
</script>

<style>
.p2p-filters{display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end}
.p2p-filters .p2p-label{flex:0 1 170px;min-width:0}
.p2p-filters .p2p-filter-grow{flex:1 1 240px}
.p2p-post-group{gap:12px}
@media (max-width:600px){
  .p2p-filters{display:grid;grid-template-columns:1fr 1fr;gap:10px}
  .p2p-filters .p2p-label{min-width:0}
  .p2p-filters .p2p-filter-grow{grid-column:1 / -1}
  .p2p-filters .p2p-input,.p2p-filters .p2p-select{width:100%;min-width:0}
}
</style>
